import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import moment from 'moment';
import axios from 'axios';
import ls from 'localstorage-slim';
import { toast } from 'react-toastify';
import { FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import DataContext from '../context/InvoiceData';
import { refreshToken, updateUserLastLogin } from '../helpers';

function BasicInfo() {
  const {
    step,
    setStep,
    documentType,
    documentTypeVersion,
    setDocumentType,
    setDocumentTypeVersion,
    setInvoiceSubType,
  } = useContext(DataContext);

  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [basicInfoData, setBasicInfoData] = useState({});

  const subDomain = ls.get('subDomain');

  useEffect(() => {
    async function fetchData() {
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoices/${invoiceId}`
        );

        setBasicInfoData(response.data);
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
        }
      }
    }

    fetchData();
  }, []);

  let invoiceId = localStorage.getItem('id');

  const maxDate = moment(basicInfoData.date_time_issued, true).utc().format('YYYY-MM-DDTHH:mm');
  const minDate = moment(basicInfoData.date_time_issued, true).utc().format('YYYY-MM-DDTHH:mm');

  const handleChange = event => {
    setBasicInfoData({ ...basicInfoData, [event.target.name]: event.target.value });
  };

  const onChange = nextStep => {
    setStep(nextStep < 0 ? 0 : nextStep > 5 ? 5 : nextStep);
  };

  const handleClose = () => {
    setStep(0);
    setDocumentTypeVersion('');
    setDocumentType('');
    setInvoiceSubType('');
    ls.remove('id');
    navigate('../../document/new');
  };

  const handleContinue = () => {
    const updateToast = toast.loading('Please wait...');

    axios
      .put(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoices/${invoiceId}`,
        basicInfoData
      )
      .then(response => {
        toast.update(updateToast, {
          render: `${response.data['message']}`,
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
        onChange(step + 1);
      })
      .catch(error => {
        toast.update(updateToast, {
          render: `${Object.values(error.response.data)[0]}`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      });
  };

  return (
    <>
      <div className={`${i18n.language === 'ar' && 'text-end'} card border-0 shadow m-3`}>
        <div className='card-body body m-5'>
          <span className='p-2 fw-bold fs-5'>{`${documentType} | ${documentTypeVersion}`}</span>
          <div className={`${i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'} mt-3`}>
            <div
              className={`${
                ['Credit Note', 'Debit Note'].includes(documentType) ? 'col-4' : 'd-none'
              } bg-secondary bg-opacity-10`}>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row reverse'
                } my-3 p-2`}>
                <span
                  className='subtitle text-decoration-underline pb-2'
                  style={{ textUnderlineOffset: '10px' }}>
                  <strong className='fs-5'>References</strong>
                </span>

                <div className='input-group mt-3 mb-2'>
                  <select
                    id='taxpayerActivityCode'
                    className='form-select'
                    aria-label='Default select example'
                    name='taxpayerActivityCode'
                    // onChange={handleActivityCode}
                  >
                    <option data-content='House Number Street<br>City, State<br>Zip Code'>
                      Select Reference
                    </option>
                    {/* {activityCodes.map(activityCode => (
                    <option key={activityCode.id} value={activityCode.id}>
                      {activityCode.code}
                    </option>
                  ))} */}
                  </select>
                  <span
                    className='input-group-text'
                    id='basic-addon1'
                    role='button'
                    // onClick={handleSearchReceiver}
                  >
                    {<FaSearch />}
                  </span>
                </div>
              </div>
            </div>
            <div className={['Credit Note', 'Debit Note'].includes(documentType) ? 'col-8' : 'col'}>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                } my-3 p-2`}>
                <span
                  className={`${
                    i18n.language === 'en' ? '' : 'arabic'
                  } subtitle text-decoration-underline pb-2`}
                  style={{ textUnderlineOffset: '10px' }}>
                  <strong className='fs-5'>{t('stepper.basicInfo.title')} </strong>
                </span>
              </div>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse flex-wrap'
                }`}>
                <label htmlFor='internalId' className='form-label mx-2'>
                  {t('stepper.basicInfo.id.title')}
                </label>
                <div
                  className={`${
                    i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                  } input-group mx-2 mb-3 align-items-center`}>
                  <input
                    id='internalId'
                    name='internal_id'
                    type='text'
                    pattern='^[aA-zZ\d- ]+$'
                    title='Internal Id should only letters, numbers. e.g. B1- 21353'
                    className='form-control col-4'
                    placeholder='Internal Id'
                    defaultValue={basicInfoData.internal_id}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChange}
                  />
                  <span className={`${i18n.language === 'ar' && 'text-end pe-2'} col-8`}>
                    {t('stepper.basicInfo.id.description')}
                  </span>
                </div>
              </div>
              <div
                className={`${
                  i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse flex-wrap'
                }`}>
                <label htmlFor='dateTimeIssued' className='form-label mx-2'>
                  {t('stepper.basicInfo.dateTime.title')}
                </label>
                <div
                  className={`${
                    i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                  } input-group mx-2 mb-3 align-items-center`}>
                  <input
                    type='datetime-local'
                    id='dateTimeIssued'
                    name='date_time_issued'
                    className={`${i18n.language === 'ar' && 'text-end'} form-control col-4`}
                    max={maxDate}
                    min={minDate}
                    value={maxDate}
                    onChange={handleChange}
                  />
                  <span className={`${i18n.language === 'ar' && 'text-end pe-2'} col-8`}>
                    {t('stepper.basicInfo.dateTime.description')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${
          i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
        } justify-content-evenly fixed-bottom py-3 bg-secondary bg-gradient bg-opacity-10`}>
        <button
          className={`${
            i18n.language === 'ar' && 'offset-6'
          } button text-center col-1 bg-white text-dark border border-1 border-dark`}
          onClick={handleClose}>
          {t('stepper.footer.close')}
        </button>
        <button
          className={`${i18n.language === 'en' && 'offset-6'} button text-center col-1 ${
            step === 0 ? 'bg-secondary bg-gradient' : ''
          }`}
          disabled={step === 0}>
          {t('stepper.footer.previous')}
        </button>
        <button className='button text-center col-1' onClick={handleContinue} disabled={step === 5}>
          {t('stepper.footer.continue')}
        </button>
      </div>
    </>
  );
}

export default BasicInfo;
